import type { DailyAnalyticsPoint, DashboardOverview } from "@/types/domain";
import type { SeriesPoint } from "@/components/charts/LineAreaChart";
import type { DonutSlice } from "@/components/charts/DonutChart";

export type AnalyticsViewMode = "single" | "7d" | "30d";
export type GrowthMetric = "drivers" | "customers";
export type BreakdownMetric = "rides" | "revenue";

export const CHART_COLORS = {
  drivers: "#0F6B5C",
  customers: "#D9A125",
  completed: "#0F6B5C",
  ongoing: "#D9A125",
  cancelled: "#C0453A",
  driverPayout: "#0F6B5C",
  commission: "#D9A125",
  refunds: "#C0453A",
} as const;

export function formatShortDate(isoDate: string): string {
  if (!isoDate) return "";
  const parts = isoDate.split("-");
  if (parts.length < 3) return isoDate;
  const monthNames = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];
  const m = parseInt(parts[1], 10) - 1;
  const d = parseInt(parts[2], 10);
  return `${d} ${monthNames[m] ?? parts[1]}`;
}

export function getDaysAgoIso(daysAgo: number): string {
  // Use IST (UTC+5:30) to match the backend's $dateToString timezone,
  // so the date keys are always consistent regardless of wall-clock time.
  const ist = new Date(Date.now() + 5.5 * 60 * 60 * 1000);
  ist.setUTCDate(ist.getUTCDate() - daysAgo);
  return ist.toISOString().slice(0, 10);
}

/**
 * Resolves the bar chart data for growth based on selected mode, date, and metric.
 * Returns null when the selected single date is not within the available 30-day window
 * (i.e. the backend has no row for that date at all), so the caller can show
 * a "Date out of range" empty state instead of a misleading 0-bar.
 */
export function resolveGrowthSeries(
  days: DailyAnalyticsPoint[] | undefined,
  mode: AnalyticsViewMode,
  selectedDate: string,
  metric: GrowthMetric,
): SeriesPoint[] | null {
  if (!days || days.length === 0) {
    return null;
  }

  if (mode === "single") {
    const point = days.find((p) => p.date === selectedDate);
    // If the date is outside the 30-day window, signal "no data for this date"
    if (!point) return null;
    // Return a single bar even if the value is 0 (0 registrations IS valid data)
    return [{ label: formatShortDate(selectedDate), value: point[metric] }];
  }

  const subset = mode === "7d" ? days.slice(-7) : days;
  return subset.map((p) => ({
    label: formatShortDate(p.date),
    value: p[metric],
  }));
}

/**
 * Resolves the donut chart slices for breakdown based on mode, date, and metric.
 */
export function resolveBreakdownSeries(
  days: DailyAnalyticsPoint[] | undefined,
  mode: AnalyticsViewMode,
  selectedDate: string,
  metric: BreakdownMetric,
  kpisFallback?: DashboardOverview["kpis"],
): DonutSlice[] {
  if (days && days.length > 0) {
    if (mode === "single") {
      const point = days.find((p) => p.date === selectedDate);
      if (metric === "rides") {
        return [
          { label: "Completed", value: point?.rides.completed ?? 0, color: CHART_COLORS.completed },
          { label: "Ongoing", value: point?.rides.ongoing ?? 0, color: CHART_COLORS.ongoing },
          { label: "Cancelled", value: point?.rides.cancelled ?? 0, color: CHART_COLORS.cancelled },
        ];
      }
      return [
        { label: "Driver Payout", value: point?.revenue.driverPayout ?? 0, color: CHART_COLORS.driverPayout },
        { label: "Commission", value: point?.revenue.commission ?? 0, color: CHART_COLORS.commission },
        { label: "Refunds", value: point?.revenue.refunds ?? 0, color: CHART_COLORS.refunds },
      ];
    }

    // Aggregated mode (7d or 30d)
    const subset = mode === "7d" ? days.slice(-7) : days;

    if (metric === "rides") {
      const totals = subset.reduce(
        (acc, curr) => {
          acc.completed += curr.rides.completed;
          acc.ongoing += curr.rides.ongoing;
          acc.cancelled += curr.rides.cancelled;
          return acc;
        },
        { completed: 0, ongoing: 0, cancelled: 0 },
      );

      return [
        { label: "Completed", value: totals.completed, color: CHART_COLORS.completed },
        { label: "Ongoing", value: totals.ongoing, color: CHART_COLORS.ongoing },
        { label: "Cancelled", value: totals.cancelled, color: CHART_COLORS.cancelled },
      ];
    }

    const totals = subset.reduce(
      (acc, curr) => {
        acc.driverPayout += curr.revenue.driverPayout;
        acc.commission += curr.revenue.commission;
        acc.refunds += curr.revenue.refunds;
        return acc;
      },
      { driverPayout: 0, commission: 0, refunds: 0 },
    );

    return [
      { label: "Driver Payout", value: totals.driverPayout, color: CHART_COLORS.driverPayout },
      { label: "Commission", value: totals.commission, color: CHART_COLORS.commission },
      { label: "Refunds", value: totals.refunds, color: CHART_COLORS.refunds },
    ];
  }

  // Graceful fallback for single-day today if analytics array is not yet provided
  if (kpisFallback && mode === "single" && selectedDate === getDaysAgoIso(0)) {
    if (metric === "rides") {
      const completed = Math.max(0, kpisFallback.ridesToday - kpisFallback.cancelledToday);
      return [
        { label: "Completed", value: completed, color: CHART_COLORS.completed },
        { label: "Ongoing", value: kpisFallback.activeRides, color: CHART_COLORS.ongoing },
        { label: "Cancelled", value: kpisFallback.cancelledToday, color: CHART_COLORS.cancelled },
      ];
    }

    const driverPayout = Math.max(0, kpisFallback.gmvToday - kpisFallback.commissionToday);
    return [
      { label: "Driver Payout", value: driverPayout, color: CHART_COLORS.driverPayout },
      { label: "Commission", value: kpisFallback.commissionToday, color: CHART_COLORS.commission },
      { label: "Refunds", value: 0, color: CHART_COLORS.refunds },
    ];
  }

  return [];
}

/**
 * For the GROWTH bar chart: only show empty state when data is null/empty.
 * A series of all-zero bars is still valid (no new registrations that day/period).
 */
export function isGrowthSeriesEmpty(data: SeriesPoint[] | null): boolean {
  return data === null || data.length === 0;
}

/**
 * For the BREAKDOWN donut chart: show empty state when all slice values are 0
 * (a donut with 0% everywhere is meaningless).
 */
export function isBreakdownSeriesEmpty(data: { value: number }[]): boolean {
  if (!data || data.length === 0) return true;
  return data.every((item) => !item.value || item.value === 0);
}

/**
 * @deprecated Use isGrowthSeriesEmpty or isBreakdownSeriesEmpty instead.
 */
export function isSeriesEmpty(data: { value: number }[] | null): boolean {
  if (!data || data.length === 0) return true;
  return data.every((item) => !item.value || item.value === 0);
}
